// client/src/components/pages/LexiStar/Replay/ReplayTimeline.tsx
//
// The replay timeline (LS-09 §5.6; FR-RPL-001/004): stage bands, deviation
// spans (approved hatched, unapproved solid) and event markers on one time
// axis, with the playhead. The band strip is the scrubber — press or drag to
// seek; as a keyboard slider ← → step a minute (Shift: ten), Home / End jump.
// Event markers are buttons in their own row (not inside the slider): selecting
// one seeks there and shows its detail.
//
// ST-00245: the scrubber owns horizontal touch gestures (touch-action: pan-y),
// so a finger drag scrubs instead of being taken over by the browser as a pan
// (which cancelled the drag after a few pixels); onScrubStart / onScrubEnd let
// the clock hold still under the pointer; the release position is applied too.
// ST-00243: lanes, playhead (with a grab handle) and event marks use theme
// tokens and solid marks that stay visible in light and dark; multi-day axes
// label midnight with the date.
// Converted from ReplayTimeline.jsx in the same change (CLAUDE.md TypeScript rule).
// ST-00276 (map colours & states): with the organisation's own display rules a "Map colour" lane
// shows the colour the rules in force gave the asset over time (./replayDisplay displayBands).

import { type KeyboardEvent, type PointerEvent, type ReactNode, useCallback, useRef } from 'react';
import { STAGE_LABELS, severityMeta } from '../StatusBoard/statusLanguage';
import { eventTypeLabel } from '../../../../utils/eventTypeLabels';
import {
  stageSurfaceColor,
  timeTicks,
  tickLabel,
  clockTime,
  clockDateTime,
  type ReplayEvent,
} from './replayLogic';
import type { ReplayDeviation, StageBand } from '../../../../types/lexistar-replay';
import { assetStatusVar, type AssetStatusKey } from '../../../map/assetStatus';
import type { DisplayBand } from './replayDisplay';

const pct = (t: number, start: number, end: number) =>
  `${Math.max(0, Math.min(100, ((t - start) / (end - start)) * 100))}%`;
const width = (a: number, b: number, start: number, end: number) =>
  `${Math.max(0.25, Math.min(100, ((Math.min(b, end) - Math.max(a, start)) / (end - start)) * 100))}%`;

const MINUTE = 60000;
const DEVIATION = '#d946ef';

// Solid event marks (the chip tints of SEVERITY_META are for text chips; as an
// 8 px mark they vanish on the surface). Each holds ≥ 3:1 against the surface
// in both themes.
const EVENT_MARK: Record<string, string> = {
  C: 'bg-red-600',
  H: 'bg-orange-600',
  M: 'bg-amber-600',
  L: 'bg-blue-600',
  I: 'bg-slate-500',
};

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-20 flex-shrink-0 text-[10px] font-semibold uppercase tracking-wider text-slate-500 text-right">
        {label}
      </span>
      <div className="relative flex-1 h-5">{children}</div>
    </div>
  );
}

export interface ReplayTimelineProps {
  start: number;
  end: number;
  cursor: number;
  onSeek: (t: number) => void;
  /** A press / drag on the scrubber began (the clock should hold still). */
  onScrubStart?: () => void;
  /** The press / drag ended (released, cancelled or capture lost). */
  onScrubEnd?: () => void;
  bands?: StageBand[];
  deviations?: ReplayDeviation[];
  events?: ReplayEvent[];
  selectedEventId?: string;
  onSelectEvent?: (e: ReplayEvent) => void;
  /** ST-00276: the map colour over time (shown when the organisation has its own rules). */
  displayBands?: DisplayBand[];
}

export default function ReplayTimeline({
  start,
  end,
  cursor,
  onSeek,
  onScrubStart,
  onScrubEnd,
  bands = [],
  deviations = [],
  events = [],
  selectedEventId,
  onSelectEvent,
  displayBands,
}: ReplayTimelineProps) {
  const areaRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ pointerId: number | undefined } | null>(null);

  /** The time under a client x, or null when the scrub area has no size yet. */
  const tAtX = useCallback(
    (clientX: number): number | null => {
      const el = areaRef.current;
      if (!el || !Number.isFinite(clientX)) return null;
      const r = el.getBoundingClientRect();
      if (!r.width) return null;
      const f = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
      return start + f * (end - start);
    },
    [start, end]
  );
  const seekToX = (clientX: number) => {
    const t = tAtX(clientX);
    if (t !== null) onSeek(t);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== undefined && e.button !== 0) return; // primary button / touch / pen only
    drag.current = { pointerId: e.pointerId };
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      /* no capture (synthetic pointer) */
    }
    onScrubStart?.();
    seekToX(e.clientX);
  };
  const isOurs = (e: PointerEvent<HTMLDivElement>) =>
    drag.current !== null &&
    (drag.current.pointerId === undefined ||
      e.pointerId === undefined ||
      e.pointerId === drag.current.pointerId);
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (isOurs(e)) seekToX(e.clientX);
  };
  const endDrag = (e: PointerEvent<HTMLDivElement>, apply: boolean) => {
    if (!isOurs(e)) return;
    if (apply) seekToX(e.clientX);
    drag.current = null;
    onScrubEnd?.();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 10 * MINUTE : MINUTE;
    let next: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = cursor + step;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = cursor - step;
    else if (e.key === 'PageUp') next = cursor + 60 * MINUTE;
    else if (e.key === 'PageDown') next = cursor - 60 * MINUTE;
    else if (e.key === 'Home') next = start;
    else if (e.key === 'End') next = end;
    if (next === null) return;
    e.preventDefault();
    onSeek(Math.max(start, Math.min(end, next)));
  };

  const ticks = timeTicks(start, end);
  const stageLabels = STAGE_LABELS as Record<string, string>;

  return (
    <div className="select-none">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-20 flex-shrink-0" />
        <div className="relative flex-1 h-4 text-[10px] text-slate-500 tabular-nums">
          {ticks.map((t, i) => (
            <span
              key={t}
              // On a phone every other label (the lane is ~240 px wide).
              className={`absolute -translate-x-1/2 whitespace-nowrap ${ticks.length > 5 && i % 2 === 1 ? 'hidden sm:block' : ''}`}
              style={{ left: pct(t, start, end) }}
            >
              {tickLabel(t)}
            </span>
          ))}
        </div>
      </div>

      <div
        role="slider"
        tabIndex={0}
        aria-label="Replay position"
        aria-valuemin={start}
        aria-valuemax={end}
        aria-valuenow={Math.round(cursor)}
        aria-valuetext={clockDateTime(cursor)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={(e) => endDrag(e, true)}
        onPointerCancel={(e) => endDrag(e, false)}
        onLostPointerCapture={(e) => endDrag(e, false)}
        onKeyDown={onKeyDown}
        className="relative space-y-1 py-1 rounded-md cursor-pointer active:cursor-grabbing touch-pan-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        <Row label="Stage">
          <div className="absolute inset-0 rounded bg-slate-100 ring-1 ring-inset ring-slate-300" />
          {bands.map((b, i) => (
            <span
              key={`${b.stage}-${b.from}-${i}`}
              className="absolute top-0 bottom-0 rounded-sm"
              style={{
                left: pct(b.from, start, end),
                width: width(b.from, b.to, start, end),
                background: stageSurfaceColor(b.stage),
              }}
              title={`${(b.stage && stageLabels[b.stage]) || b.stage}: ${clockTime(b.from, false)}–${clockTime(b.to, false)}`}
            />
          ))}
        </Row>
        {displayBands && displayBands.length > 0 && (
          <Row label="Map colour">
            <div className="absolute inset-0 rounded bg-slate-100 ring-1 ring-inset ring-slate-300" />
            {displayBands.map((b, i) => (
              <span
                key={`${b.display.base.rule_id}-${b.from}-${i}`}
                className="absolute top-0 bottom-0 rounded-sm"
                data-display-band={b.display.base.token}
                style={{
                  left: pct(b.from, start, end),
                  width: width(b.from, Math.max(b.to, b.from + 1), start, end),
                  background: assetStatusVar(b.display.base.token as AssetStatusKey, 'solid'),
                }}
                title={`${b.display.base.label}: ${clockTime(b.from, false)}–${clockTime(b.to, false)}`}
              />
            ))}
          </Row>
        )}
        <Row label="Deviations">
          <div className="absolute inset-x-0 top-1/2 h-px bg-slate-300" />
          {deviations.map((d, i) => (
            <span
              key={d.id || i}
              className="absolute top-0.5 bottom-0.5 rounded-sm"
              style={{
                left: pct(d.from, start, end),
                width: width(d.from, Number.isFinite(d.to) ? d.to : d.from + MINUTE, start, end),
                background: d.approved
                  ? `repeating-linear-gradient(135deg,${DEVIATION} 0 4px,transparent 4px 7px)`
                  : DEVIATION,
                boxShadow: d.approved ? `inset 0 0 0 1px ${DEVIATION}` : undefined,
              }}
              title={`${d.approved ? 'Approved detour' : 'Unapproved deviation'} ${clockTime(d.from, false)}–${clockTime(d.to, false)}`}
            />
          ))}
        </Row>

        <div
          ref={areaRef}
          className="pointer-events-none absolute top-0 bottom-0 right-0"
          style={{ left: '5.5rem' }}
          aria-hidden="true"
        >
          <div
            className="absolute top-0 bottom-0 flex flex-col items-center -translate-x-1/2"
            style={{ left: pct(cursor, start, end) }}
            data-testid="replay-playhead"
          >
            <span className="-mt-1 w-3 h-3 flex-shrink-0 rounded-full bg-slate-900 ring-2 ring-surface shadow" />
            <span className="flex-1 w-0.5 bg-slate-900" />
          </div>
        </div>
      </div>
      <div className="relative mt-1" role="group" aria-label="Event markers">
        <Row label="Events">
          <div className="absolute inset-x-0 top-1/2 h-px bg-slate-300" />
          <div
            className="pointer-events-none absolute top-0 bottom-0 w-0.5 bg-slate-900/40 -translate-x-1/2"
            style={{ left: pct(cursor, start, end) }}
            aria-hidden="true"
          />
          {events.map((e) => {
            const meta = severityMeta(e.severity);
            const selected = e.id === selectedEventId;
            return (
              <button
                key={e.id || e.t}
                type="button"
                onClick={() => onSelectEvent?.(e)}
                aria-label={`${eventTypeLabel(e.type)} at ${clockTime(e.t)}`}
                aria-pressed={selected}
                title={`${eventTypeLabel(e.type)} · ${meta.label} · ${clockTime(e.t)}`}
                className={`absolute top-0 -translate-x-1/2 w-2 h-5 rounded-sm ${EVENT_MARK[String(e.severity)] || EVENT_MARK.I} ${selected ? 'ring-2 ring-offset-1 ring-offset-surface ring-slate-900' : 'ring-1 ring-surface'} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500`}
                style={{ left: pct(e.t, start, end) }}
              />
            );
          })}
        </Row>
      </div>
    </div>
  );
}
